// Types
import {
  MyTicketsInput,
  SortDirection,
  TicketOrderStatus,
  TicketStatus,
} from 'api/graphql-global-types';
import {
  MyTicketFields,
  MyTicketFields_ticketEvent,
} from 'api/ticketEvents/types/MyTicketFields';
import { StatusVariant } from 'ui3/Status/Status';
// Helpers
import { formatTicketEventDateWithZone } from 'helpers/ticketSales';

export type MyTicket = MyTicketFields;
export type MyTicketEvent = MyTicketFields_ticketEvent;

export type MyTicketBadge = { label: string; variant: StatusVariant };
export type MyTicketTierLine = { tierLabel: string; count: number };
export type MyTicketEventGroup = { event: MyTicketEvent; tickets: MyTicket[] };

// Failed and still-processing orders stay listed so the fan learns how a payment ended.
export const MY_TICKET_ORDER_STATUSES = [
  TicketOrderStatus.Confirmed,
  TicketOrderStatus.PendingPayment,
  TicketOrderStatus.PaymentReview,
  TicketOrderStatus.RefundPending,
  TicketOrderStatus.Refunded,
  TicketOrderStatus.Cancelled,
  TicketOrderStatus.Expired,
];

/** The API's page size cap. */
export const MY_TICKETS_LIMIT = 100;

/** Every ticket the user holds; shared so the event page and Tickets tab read one cached list. */
export const MY_HELD_TICKETS_INPUT: MyTicketsInput = {
  limit: MY_TICKETS_LIMIT,
  offset: 0,
  direction: SortDirection.DESC,
};

/** Payment Failed covers both statuses that issued no tickets. */
export type TicketOrderStatusFilter =
  | ''
  | TicketOrderStatus.Confirmed
  | TicketOrderStatus.PendingPayment
  | TicketOrderStatus.PaymentReview
  | TicketOrderStatus.RefundPending
  | TicketOrderStatus.Refunded
  | TicketOrderStatus.Cancelled;

export const TICKET_ORDER_STATUS_FILTER_OPTIONS: {
  value: TicketOrderStatusFilter;
  label: string;
}[] = [
  { value: '', label: 'All Statuses' },
  { value: TicketOrderStatus.Confirmed, label: 'Confirmed' },
  { value: TicketOrderStatus.PendingPayment, label: 'Processing' },
  { value: TicketOrderStatus.PaymentReview, label: 'Payment Review' },
  { value: TicketOrderStatus.RefundPending, label: 'Refund Pending' },
  { value: TicketOrderStatus.Refunded, label: 'Refunded' },
  { value: TicketOrderStatus.Cancelled, label: 'Payment Failed' },
];

export const getTicketOrderFilterStatuses = (
  filter: TicketOrderStatusFilter
): TicketOrderStatus[] => {
  if (!filter) {
    return MY_TICKET_ORDER_STATUSES;
  }

  return filter === TicketOrderStatus.Cancelled
    ? [TicketOrderStatus.Cancelled, TicketOrderStatus.Expired]
    : [filter];
};

const ORDER_STATUS_BADGES: Partial<Record<TicketOrderStatus, MyTicketBadge>> = {
  [TicketOrderStatus.Confirmed]: { label: 'Confirmed', variant: 'approved' },
  [TicketOrderStatus.PendingPayment]: {
    label: 'Processing',
    variant: 'applied',
  },
  [TicketOrderStatus.PaymentReview]: {
    label: 'Payment Review',
    variant: 'applied',
  },
  [TicketOrderStatus.RefundPending]: {
    label: 'Refund Pending',
    variant: 'applied',
  },
  [TicketOrderStatus.Refunded]: { label: 'Refunded', variant: 'declined' },
  [TicketOrderStatus.Cancelled]: {
    label: 'Payment Failed',
    variant: 'declined',
  },
  [TicketOrderStatus.Expired]: {
    label: 'Payment Failed',
    variant: 'declined',
  },
};

const TICKET_STATUS_BADGES: Partial<Record<TicketStatus, MyTicketBadge>> = {
  [TicketStatus.Admitted]: { label: 'Checked in', variant: 'approved' },
  [TicketStatus.Revoked]: { label: 'Revoked', variant: 'declined' },
};

export const getTicketOrderBadge = (
  status: TicketOrderStatus
): MyTicketBadge | null => ORDER_STATUS_BADGES[status] ?? null;

export const getTicketBadge = (status: TicketStatus): MyTicketBadge | null =>
  TICKET_STATUS_BADGES[status] ?? null;

/** Why a ticket shows no QR code. */
export const TICKET_UNAVAILABLE_MESSAGES: Partial<
  Record<TicketStatus, string>
> = {
  [TicketStatus.Admitted]: 'This ticket has already been checked in.',
  [TicketStatus.Revoked]: 'This ticket has been revoked.',
};

/** What happened to an order that failed or is being refunded. */
export const TICKET_ORDER_NOTICES: Partial<
  Record<TicketOrderStatus, string>
> = {
  [TicketOrderStatus.Cancelled]:
    "This payment didn't go through, so no tickets were issued.",
  [TicketOrderStatus.Expired]:
    "This payment didn't go through in time, so no tickets were issued.",
  [TicketOrderStatus.RefundPending]:
    "We're refunding this order. Its tickets can't be used or sent while the refund goes through.",
  [TicketOrderStatus.Refunded]:
    'This order was refunded, so its tickets no longer work.',
};

export const PROCESSING_TICKET_ORDER_NOTICE =
  "We're confirming your payment. Your tickets will show up here once it clears.";

export const formatTicketCount = (count: number): string =>
  `${count} ${count === 1 ? 'Ticket' : 'Tickets'}`;

/** "You have 2 tickets" */
export const formatHeldTicketCount = (count: number): string =>
  `You have ${count} ${count === 1 ? 'ticket' : 'tickets'}`;

/** "Tue, Oct 08 • 08:00 PM ET", in the venue's time zone. */
export const formatMyTicketEventDate = (
  startsAt: string,
  zone: string
): string =>
  formatTicketEventDateWithZone(startsAt, zone, 'ddd, MMM DD • hh:mm A');

/** "Tuesday, Oct 08 • 08:00 PM ET", in the venue's time zone. */
export const formatTicketPassDate = (startsAt: string, zone: string): string =>
  formatTicketEventDateWithZone(startsAt, zone, 'dddd, MMM DD • hh:mm A');

export const countTicketsByOrder = (
  tickets: MyTicket[]
): Map<string, number> => {
  const counts = new Map<string, number>();
  tickets.forEach(({ ticketOrderId }) => {
    counts.set(ticketOrderId, (counts.get(ticketOrderId) ?? 0) + 1);
  });

  return counts;
};

export const groupTicketsByTier = (tickets: MyTicket[]): MyTicketTierLine[] => {
  const counts = new Map<string, number>();
  tickets.forEach(({ tierLabel }) => {
    counts.set(tierLabel, (counts.get(tierLabel) ?? 0) + 1);
  });

  return Array.from(counts, ([tierLabel, count]) => ({ tierLabel, count }));
};

export const groupTicketsByEvent = (
  tickets: MyTicket[]
): MyTicketEventGroup[] => {
  const groups = new Map<string, MyTicketEventGroup>();
  tickets.forEach((ticket) => {
    const group = groups.get(ticket.ticketEvent.id) ?? {
      event: ticket.ticketEvent,
      tickets: [],
    };
    group.tickets.push(ticket);
    groups.set(ticket.ticketEvent.id, group);
  });

  return Array.from(groups.values());
};
